/**
 * useRequestForm Hook
 * Custom hook for managing RequestForm state and logic
 */

import { useState, useCallback, useMemo } from 'react';
import type { CalendarValue } from '@dynamon-as/ui';
import {
    Request,
    RequestType,
    RequestSource,
    ShiftPreference,
    CreateRequestDto,
    UpdateRequestDto,
} from '@features/requests/types';
import { getRequestTypeKey } from '@features/requests/utils';
import type {
    DateSelectionValue,
    DateSelectionMode,
    SingleDateSelection,
    PeriodDateSelection,
    LooseDaysDateSelection,
    ShiftDateSelection,
    Shift,
} from '@features/requests/types/dateSelectionTypes';
import { useDateSelection } from './useDateSelection';

/** Ensure date is not null/undefined */
function ensureDate(date: Date | null | undefined): Date {
    return date ?? new Date();
}

/**
 * Form errors interface
 */
export interface FormErrors {
    type?: string;
    dateSelection?: string;
    shiftSelection?: string;
    shiftPreference?: string;
}

/**
 * useRequestForm hook props
 */
export interface UseRequestFormProps {
    initialValues?: Partial<Request>;
    availableShifts?: Shift[];
    excludedDates?: Date[];
}

/**
 * useRequestForm hook return type
 */
export interface UseRequestFormReturn {
    // State
    type: RequestType;
    notes: string;
    shiftPreference: ShiftPreference | undefined;
    dateSelectionMode: DateSelectionMode;
    dateSelectionValue: DateSelectionValue;
    errors: FormErrors;
    excludedDates: Date[];

    // Derived state
    isShiftPreference: boolean;
    singleSelection: SingleDateSelection | null;
    periodSelection: PeriodDateSelection | null;
    looseDaysSelection: LooseDaysDateSelection | null;
    shiftSelection: ShiftDateSelection | null;

    // Handlers
    setType: (type: RequestType) => void;
    setNotes: (notes: string) => void;
    setShiftPreference: (shift: ShiftPreference) => void;
    setExcludedDates: (dates: Date[]) => void;
    handleDateSelectionModeChange: (newMode: DateSelectionMode) => void;
    handleCalendarChange: (newValue: CalendarValue) => void;
    handleShiftSelect: (shiftId: ShiftPreference) => void;

    // Actions
    validateForm: () => boolean;
    buildDto: () => CreateRequestDto | UpdateRequestDto;
    getDateSelectionLabel: () => string;
    getCalendarProps: () => {
        selectionType: 'single' | 'range' | 'multiple';
        value: unknown;
    };
}

/**
 * Custom hook for RequestForm state and logic
 */
export function useRequestForm({
    initialValues,
    excludedDates: initialExcludedDates,
}: UseRequestFormProps): UseRequestFormReturn {
    // Form state
    const [type, setType] = useState<RequestType>(
        initialValues?.type
            ? getRequestTypeKey(initialValues.type)
            : RequestType.VACATION,
    );
    const [notes, setNotes] = useState<string>(initialValues?.notes ?? '');
    const [shiftPreference, setShiftPreference] = useState<
        ShiftPreference | undefined
    >(initialValues?.shiftPreference);
    const [excludedDates, setExcludedDates] = useState<Date[]>(
        initialExcludedDates ?? [],
    );

    // Use date selection hook
    const {
        dateSelectionMode,
        dateSelectionValue,
        singleSelection,
        periodSelection,
        looseDaysSelection,
        shiftSelection,
        handleDateSelectionModeChange,
        handleCalendarChange,
        handleShiftSelect,
        getDateSelectionLabel,
        getCalendarProps,
    } = useDateSelection({
        initialStartDate: initialValues?.startDate ?? undefined,
        initialEndDate: initialValues?.endDate ?? undefined,
    });

    // Validation and error state
    const [errors, setErrors] = useState<FormErrors>({});

    // Derived state
    const isShiftPreference = useMemo(
        () => type === RequestType.SHIFT_PREFERENCE,
        [type],
    );

    // Validate form based on current mode
    const validateForm = useCallback(() => {
        const newErrors: FormErrors = {};

        if (!type) {
            newErrors.type = 'Søknadstype er påkrevd';
        }

        switch (dateSelectionMode) {
            case 'single':
                if (!singleSelection?.date) {
                    newErrors.dateSelection = 'Du må velge en dato';
                }
                break;
            case 'period':
                if (!periodSelection?.startDate) {
                    newErrors.dateSelection = 'Du må velge en startdato';
                } else if (!periodSelection?.endDate) {
                    newErrors.dateSelection = 'Du må velge en sluttdato';
                } else if (
                    periodSelection.startDate &&
                    periodSelection.endDate &&
                    periodSelection.endDate < periodSelection.startDate
                ) {
                    newErrors.dateSelection =
                        'Sluttdatoen må være lik eller etter startdatoen';
                }
                break;
            case 'loose_days':
                if (
                    !looseDaysSelection?.dates ||
                    looseDaysSelection.dates.length === 0
                ) {
                    newErrors.dateSelection = 'Du må velge minst én dag';
                }
                break;
            case 'shift':
                if (!shiftSelection?.shiftSelection?.date) {
                    newErrors.dateSelection = 'Du må velge en dato';
                } else if (!shiftSelection?.shiftSelection?.shiftId) {
                    newErrors.shiftSelection = 'Du må velge et skift';
                }
                break;
            default:
                break;
        }

        if (
            isShiftPreference &&
            dateSelectionMode !== 'shift' &&
            !shiftPreference
        ) {
            newErrors.shiftPreference = 'Skiftpreferanse er påkrevd';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [
        type,
        dateSelectionMode,
        singleSelection,
        periodSelection,
        looseDaysSelection,
        shiftSelection,
        isShiftPreference,
        shiftPreference,
    ]);

    // Build DTO based on current selection
    const buildDto = useCallback(() => {
        const source: RequestSource =
            type === RequestType.SHIFT_PREFERENCE
                ? RequestSource.WISH
                : (type as RequestSource);
        const baseDto: Partial<CreateRequestDto> = {
            type: source,
            notes: notes ?? undefined,
            ...(isShiftPreference && shiftPreference && { shiftPreference }),
            excludedDates,
        };

        switch (dateSelectionMode) {
            case 'single':
                return {
                    ...baseDto,
                    startDate: ensureDate(singleSelection?.date),
                    endDate: ensureDate(singleSelection?.date),
                } as CreateRequestDto;
            case 'period':
                return {
                    ...baseDto,
                    startDate: ensureDate(periodSelection?.startDate),
                    endDate: ensureDate(periodSelection?.endDate),
                } as CreateRequestDto;
            case 'loose_days':
                if (
                    looseDaysSelection?.dates &&
                    looseDaysSelection.dates.length > 0
                ) {
                    const sortedDates = [...looseDaysSelection.dates].sort(
                        (a, b) => a.getTime() - b.getTime(),
                    );
                    return {
                        ...baseDto,
                        startDate: sortedDates[0],
                        endDate: sortedDates[sortedDates.length - 1],
                        notes: `${baseDto.notes ?? ''} [Enkeltdager: ${sortedDates.length} dager]`.trim(),
                    } as CreateRequestDto;
                }
                return {
                    ...baseDto,
                    startDate: new Date(),
                    endDate: new Date(),
                } as CreateRequestDto;
            case 'shift':
                if (shiftSelection?.shiftSelection?.date) {
                    return {
                        ...baseDto,
                        startDate: shiftSelection.shiftSelection.date,
                        endDate: shiftSelection.shiftSelection.date,
                        shiftPreference: shiftSelection.shiftSelection
                            .shiftId as ShiftPreference,
                    } as CreateRequestDto;
                }
                return {
                    ...baseDto,
                    startDate: new Date(),
                    endDate: new Date(),
                } as CreateRequestDto;
            default:
                return {
                    ...baseDto,
                    startDate: new Date(),
                    endDate: new Date(),
                } as CreateRequestDto;
        }
    }, [
        type,
        notes,
        dateSelectionMode,
        singleSelection,
        periodSelection,
        looseDaysSelection,
        shiftSelection,
        isShiftPreference,
        shiftPreference,
        excludedDates,
    ]);

    return {
        // State
        type,
        notes,
        shiftPreference,
        dateSelectionMode,
        dateSelectionValue,
        errors,
        excludedDates,

        // Derived state
        isShiftPreference,
        singleSelection,
        periodSelection,
        looseDaysSelection,
        shiftSelection,

        // Handlers
        setType,
        setNotes,
        setShiftPreference,
        setExcludedDates,
        handleDateSelectionModeChange,
        handleCalendarChange,
        handleShiftSelect,

        // Actions
        validateForm,
        buildDto,
        getDateSelectionLabel,
        getCalendarProps,
    };
}

export default useRequestForm;
